import React, { useState, useEffect } from 'react';
import {
  X, Package, QrCode, Loader2, CheckSquare, AlertTriangle,
  RotateCcw, ExternalLink, CheckCircle2, Clock, User, Bell, ZoomIn, Phone,
  ChevronLeft, ChevronRight,
} from 'lucide-react';
import { cn } from '../../../utils/cn';
import {
  getEncomenda,
  updateEncomendaStatus,
  notifyEncomenda,
  Encomenda,
  EncomendaStatus,
  EncomendaEvent,
  encomendaPhotoUrls,
} from '../../../services/encomendaApi';
import { makeAbsolutePhotoUrl } from '../../../services/api';
import EncomendaCheckoutModal from './EncomendaCheckoutModal';
import EncomendaQrModal from './EncomendaQrModal';
import toast from 'react-hot-toast';
import { CondoType, formatUnitLabel } from '../../../utils/terminology';

interface EncomendaDetailModalProps {
  open: boolean;
  encomendaId: string | null;
  customerId?: string;
  departments?: { id: string; name: string }[];
  condoType?: CondoType;
  onClose: () => void;
  onStatusChange?: () => void;
  onOpenUnit?: (unitId: string, unitName: string, blockId?: string) => void;
  onPersonClick?: (personId: string) => void;
}

function encomendaUnitLabel(unit: NonNullable<Encomenda['unit']>, condoType?: CondoType): string {
  const streetLine = unit.street?.name
    ? (unit.street_number ? `${unit.street.name}, ${unit.street_number}` : unit.street.name)
    : null;
  const blockLine = formatUnitLabel({ number: unit.number, blockName: unit.block?.name }, condoType);
  return [streetLine, blockLine].filter(Boolean).join(' – ');
}

function elapsed(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const totalMin = Math.floor(diff / 60000);
  const hours = Math.floor(totalMin / 60);
  const mins = totalMin % 60;
  if (hours >= 48) return `${Math.floor(hours / 24)}d`;
  if (hours >= 1) return `${hours}h ${mins}min`;
  return `${Math.max(1, mins)}min`;
}

const STATUS_LABELS: Record<string, string> = {
  received: 'Aguardando retirada',
  notified: 'Morador notificado',
  picked_up: 'Retirada',
  returned: 'Devolvida',
};

const STATUS_DOT: Record<string, string> = {
  received: 'bg-amber-400',
  notified: 'bg-blue-400',
  picked_up: 'bg-emerald-500',
  returned: 'bg-gray-400',
};

const EVENT_LABELS: Record<string, string> = {
  received:  'Encomenda recebida',
  notified:  'Morador notificado',
  picked_up: 'Retirada pelo destinatário',
  returned:  'Devolvida ao remetente',
  push_sent: 'Notificação enviada',
};

const EVENT_DOT: Record<string, string> = {
  received:  'bg-amber-400',
  notified:  'bg-blue-400',
  picked_up: 'bg-emerald-500',
  returned:  'bg-gray-400',
  push_sent: 'bg-violet-400',
};

function InfoField({ label, value, mono = false }: { label: string; value: React.ReactNode; mono?: boolean }) {
  return (
    <div>
      <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-1">{label}</p>
      <p className={cn('text-sm font-medium text-gray-900 break-all', mono && 'font-mono')}>{value}</p>
    </div>
  );
}

function EventRow({
  ev,
  isLast,
  onPersonClick,
  person,
}: {
  ev: EncomendaEvent;
  isLast: boolean;
  onPersonClick?: (id: string) => void;
  person?: { id: string; first_name: string; last_name: string } | null;
}) {
  const label = EVENT_LABELS[ev.event_type] ?? ev.event_type;
  const dot = EVENT_DOT[ev.event_type] ?? 'bg-gray-300';
  const date = new Date(ev.created_at).toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });

  const showOperator = ev.created_by_name && (
    ev.event_type === 'received' ||
    ev.event_type === 'picked_up' ||
    ev.event_type === 'returned'
  );

  return (
    <div className="flex gap-3">
      <div className="flex flex-col items-center flex-shrink-0 pt-1.5">
        <div className={cn('w-2 h-2 rounded-full flex-shrink-0', dot)} />
        {!isLast && <div className="w-px flex-1 bg-gray-100 mt-1.5 min-h-[1.25rem]" />}
      </div>
      <div className="flex-1 pb-3">
        <div className="flex items-baseline justify-between gap-2">
          <div className="flex items-center gap-1.5">
            <p className="text-sm text-gray-800">{label}</p>
            {ev.event_type === 'push_sent' && person && onPersonClick && (
              <button
                onClick={() => onPersonClick(person.id)}
                className="text-xs text-blue-500 hover:text-blue-700 hover:underline transition-colors"
              >
                {person.first_name} {person.last_name}
              </button>
            )}
          </div>
          <p className="text-xs text-gray-400 flex-shrink-0 tabular-nums">{date}</p>
        </div>
        {showOperator && (
          <p className="text-[11px] text-gray-400 mt-0.5 flex items-center gap-1">
            <User className="h-3 w-3" />
            {ev.created_by_name}
          </p>
        )}
        {ev.notes && <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{ev.notes}</p>}
        {ev.signature_data && (
          <div className="mt-2">
            <p className="text-[11px] text-gray-400 mb-1.5 flex items-center gap-1">
              <CheckCircle2 className="h-3 w-3 text-emerald-500" />
              Assinatura capturada
            </p>
            <img
              src={ev.signature_data}
              alt="assinatura"
              className="w-full max-w-xs rounded-lg border border-gray-200 bg-white"
              style={{ maxHeight: 96 }}
            />
          </div>
        )}
        {!ev.signature_data && (ev.event_type === 'picked_up' || ev.event_type === 'returned') && (
          <p className="text-[11px] text-gray-400 mt-0.5 flex items-center gap-1">
            <AlertTriangle className="h-3 w-3 text-amber-400" />
            Sem assinatura
          </p>
        )}
      </div>
    </div>
  );
}

export default function EncomendaDetailModal({
  open,
  encomendaId,
  customerId,
  departments,
  condoType,
  onClose,
  onStatusChange,
  onOpenUnit,
  onPersonClick,
}: EncomendaDetailModalProps) {
  const [enc, setEnc] = useState<Encomenda | null>(null);
  const [loading, setLoading] = useState(false);
  const [checkoutAction, setCheckoutAction] = useState<'picked_up' | 'returned'>('picked_up');
  const [showCheckout, setShowCheckout] = useState(false);
  const [showQr, setShowQr] = useState(false);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [notifying, setNotifying] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  useEffect(() => {
    if (!open || !encomendaId || !customerId) return;
    setLoading(true);
    setEnc(null);
    getEncomenda(customerId, encomendaId)
      .then(r => setEnc(r.data))
      .catch(() => toast.error('Erro ao carregar encomenda'))
      .finally(() => setLoading(false));
  }, [open, encomendaId, customerId]);

  const handleNotify = async () => {
    if (!customerId || !enc) return;
    setNotifying(true);
    try {
      await notifyEncomenda(customerId, enc.id);
      toast.success('Notificação enviada!');
      const r = await getEncomenda(customerId, enc.id);
      setEnc(r.data);
    } catch {
      toast.error('Erro ao enviar notificação');
    } finally {
      setNotifying(false);
    }
  };

  const handleCheckout = async (
    status: EncomendaStatus,
    opts: { signature_data?: string; notes?: string }
  ) => {
    if (!customerId || !enc) return;
    setProcessingId(enc.id);
    try {
      await updateEncomendaStatus(customerId, enc.id, status, opts);
      toast.success(status === 'picked_up' ? 'Retirada registrada!' : 'Devolução registrada!');
      const r = await getEncomenda(customerId, enc.id);
      setEnc(r.data);
      onStatusChange?.();
    } catch (err: any) {
      toast.error(err?.message || 'Erro');
      throw err;
    } finally {
      setProcessingId(null);
    }
  };

  if (!open) return null;

  const isPending = enc && (enc.status === 'received' || enc.status === 'notified');
  const photoUrls = enc ? encomendaPhotoUrls(enc).map(makeAbsolutePhotoUrl) : [];
  const photoUrl = photoUrls[0] || null; // primary photo — used for the main column + layout below

  const isEmpresa = !enc?.unit && !!enc?.department_id;
  const unitLabel = enc?.unit
    ? encomendaUnitLabel(enc.unit, condoType)
    : isEmpresa
      ? (departments?.find(d => d.id === enc.department_id)?.name ?? '—')
      : '—';

  const personLabel = enc?.person
    ? `${enc.person.first_name} ${enc.person.last_name}` : null;

  const formatDate = (d: string) =>
    new Date(d).toLocaleString('pt-BR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });

  return (
    <>
      <EncomendaCheckoutModal
        open={showCheckout}
        encomenda={enc}
        action={checkoutAction}
        customerId={customerId}
        onConfirm={handleCheckout}
        onClose={() => setShowCheckout(false)}
      />
      <EncomendaQrModal
        open={showQr}
        encomenda={enc}
        onClose={() => setShowQr(false)}
      />

      <div className="fixed inset-0 z-[75] flex items-center justify-center p-4 bg-black/50">
        <div className="absolute inset-0" onClick={onClose} />

        <div className="relative w-full max-w-2xl bg-white border border-gray-200 rounded-2xl shadow-xl flex flex-col max-h-[92vh] overflow-hidden">

          {/* ── Header ── */}
          <div className="flex items-start gap-3 px-5 py-4 border-b border-gray-100 flex-shrink-0">
            <div className="p-2.5 bg-amber-50 rounded-xl flex-shrink-0 mt-0.5">
              <Package className="h-5 w-5 text-amber-500" />
            </div>

            <div className="flex-1 min-w-0 space-y-1">
              {/* Unit */}
              {onOpenUnit && enc?.unit ? (
                <button
                  onClick={() => onOpenUnit(
                    enc.unit!.id,
                    encomendaUnitLabel(enc.unit!, condoType),
                    enc.unit!.block?.id
                  )}
                  className="text-gray-900 font-bold text-lg leading-tight hover:text-blue-600 hover:underline underline-offset-2 transition-colors text-left flex items-center gap-1.5"
                >
                  <span>{unitLabel}</span>
                  <ExternalLink className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                </button>
              ) : (
                <p className="text-gray-900 font-bold text-lg leading-tight">{unitLabel}</p>
              )}

              {/* Person */}
              {personLabel && (
                onPersonClick && enc?.person ? (
                  <button
                    onClick={() => onPersonClick(enc.person!.id)}
                    className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-blue-600 transition-colors"
                  >
                    <User className="h-3.5 w-3.5 flex-shrink-0" />
                    <span>{personLabel}</span>
                    <ExternalLink className="h-3 w-3 opacity-30 flex-shrink-0" />
                  </button>
                ) : (
                  <p className="flex items-center gap-1.5 text-sm text-gray-500">
                    <User className="h-3.5 w-3.5 flex-shrink-0" />
                    {personLabel}
                  </p>
                )
              )}

              {/* Phone + WhatsApp */}
              {enc?.person?.phone && (() => {
                const digits = enc.person!.phone!.replace(/\D/g, '');
                const waUrl = `https://wa.me/55${digits}`;
                return (
                  <div className="flex items-center gap-2">
                    <span className="flex items-center gap-1.5 text-sm text-gray-500">
                      <Phone className="h-3.5 w-3.5 flex-shrink-0" />
                      {enc.person!.phone}
                    </span>
                    <a
                      href={waUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-[#25D366]/10 text-[#128C7E] hover:bg-[#25D366]/20 border border-[#25D366]/30 transition-colors"
                    >
                      <svg className="h-3 w-3 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                      WhatsApp
                    </a>
                  </div>
                );
              })()}

              {/* Status + elapsed */}
              {enc && (
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-500">
                    <span className={cn('w-2 h-2 rounded-full flex-shrink-0', STATUS_DOT[enc.status] ?? 'bg-gray-400')} />
                    {STATUS_LABELS[enc.status] ?? enc.status}
                  </span>
                  {isPending && (
                    <span className="inline-flex items-center gap-1 text-xs text-gray-400">
                      <Clock className="h-3 w-3" />
                      há {elapsed(enc.created_at)}
                    </span>
                  )}
                </div>
              )}
            </div>

            <div className="flex items-center gap-0.5 flex-shrink-0 self-start">
              <button
                onClick={() => setShowQr(true)}
                className="p-1.5 text-gray-400 hover:text-gray-700 rounded-lg transition-colors"
                title="QR / Imprimir"
              >
                <QrCode className="h-4.5 w-4.5" />
              </button>
              <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-700 rounded-lg transition-colors">
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* ── Body ── */}
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="h-7 w-7 animate-spin text-gray-300" />
              </div>
            ) : enc ? (
              <>
                {/* Photo + Info */}
                <div className="flex">
                  {/* Photo column — primary photo, plus a thumbnail strip for the rest when there's more than one */}
                  {photoUrl && (
                    <div className="w-56 flex-shrink-0 bg-gray-50 border-r border-gray-100 self-stretch flex flex-col">
                      <button
                        type="button"
                        onClick={() => setLightboxIndex(0)}
                        className="flex-1 relative group overflow-hidden text-left"
                      >
                        <img
                          src={photoUrl}
                          alt="encomenda"
                          className="w-full h-full object-cover"
                          style={{ minHeight: 120, maxHeight: 280 }}
                        />
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <div className="bg-white rounded-full p-2.5 shadow-xl">
                            <ZoomIn className="h-5 w-5 text-gray-700" />
                          </div>
                        </div>
                      </button>
                      {photoUrls.length > 1 && (
                        <div className="flex gap-1.5 p-1.5 flex-wrap">
                          {photoUrls.slice(1).map((url, i) => (
                            <button
                              key={url}
                              type="button"
                              onClick={() => setLightboxIndex(i + 1)}
                              className="w-10 h-10 rounded overflow-hidden border border-gray-200 hover:border-gray-400 transition-colors"
                            >
                              <img src={url} alt={`encomenda ${i + 2}`} className="w-full h-full object-cover" />
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Info */}
                  <div className="flex-1 min-w-0 px-5 py-5">
                    <div className={cn('grid gap-x-6 gap-y-4', photoUrl ? 'grid-cols-2' : 'grid-cols-3')}>
                      {enc.transportadora && <InfoField label="Transportadora" value={enc.transportadora} />}
                      {enc.tracking_code && <InfoField label="Rastreio" value={enc.tracking_code} mono />}
                      <InfoField label="Chegou em" value={formatDate(enc.created_at)} />
                      {enc.created_by_name && <InfoField label="Registrada por" value={enc.created_by_name} />}
                      {enc.picked_up_at && <InfoField label="Retirada em" value={formatDate(enc.picked_up_at)} />}
                      {enc.returned_at && <InfoField label="Devolvida em" value={formatDate(enc.returned_at)} />}
                    </div>
                    {enc.description && (
                      <div className="mt-4 pt-4 border-t border-gray-100">
                        <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-1">Descrição</p>
                        <p className="text-sm text-gray-700 leading-relaxed">{enc.description}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Timeline */}
                {enc.events && enc.events.length > 0 && (
                  <div className="px-5 py-4 border-t border-gray-100">
                    <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-3">
                      Histórico de eventos
                    </p>
                    {enc.events.map((ev, i) => (
                      <EventRow
                        key={ev.id}
                        ev={ev}
                        isLast={i === enc.events!.length - 1}
                        person={enc.person}
                        onPersonClick={onPersonClick}
                      />
                    ))}
                  </div>
                )}
              </>
            ) : null}
          </div>

          {/* ── Footer ── */}
          {enc && isPending && (
            <div className="flex gap-2 px-5 py-4 border-t border-gray-100 flex-shrink-0">
              <button
                onClick={() => { setCheckoutAction('picked_up'); setShowCheckout(true); }}
                disabled={!!processingId || notifying}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold bg-gray-900 hover:bg-gray-800 text-white transition-colors disabled:opacity-50"
              >
                <CheckSquare className="h-4 w-4" />
                Confirmar Retirada
              </button>
              <button
                onClick={() => { setCheckoutAction('returned'); setShowCheckout(true); }}
                disabled={!!processingId || notifying}
                className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold bg-white hover:bg-gray-50 text-gray-700 border border-gray-200 transition-colors disabled:opacity-50"
              >
                <RotateCcw className="h-4 w-4" />
                Devolver
              </button>
              <button
                onClick={handleNotify}
                disabled={!!processingId || notifying}
                className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold bg-violet-50 hover:bg-violet-100 text-violet-700 border border-violet-200 transition-colors disabled:opacity-50"
              >
                {notifying ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Bell className="h-4 w-4" />
                )}
                Notificar
              </button>
            </div>
          )}
        </div>
      </div>
      {/* Lightbox */}
      {lightboxIndex !== null && photoUrls[lightboxIndex] && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/92"
          onClick={() => setLightboxIndex(null)}
        >
          <button
            className="absolute top-4 right-4 text-white/60 hover:text-white transition-colors"
            onClick={() => setLightboxIndex(null)}
          >
            <X className="h-6 w-6" />
          </button>
          {photoUrls.length > 1 && (
            <>
              <button
                className="absolute left-4 top-1/2 -translate-y-1/2 text-white/60 hover:text-white transition-colors"
                onClick={e => { e.stopPropagation(); setLightboxIndex(i => (i === null ? null : (i - 1 + photoUrls.length) % photoUrls.length)); }}
              >
                <ChevronLeft className="h-8 w-8" />
              </button>
              <button
                className="absolute right-4 top-1/2 -translate-y-1/2 text-white/60 hover:text-white transition-colors"
                onClick={e => { e.stopPropagation(); setLightboxIndex(i => (i === null ? null : (i + 1) % photoUrls.length)); }}
              >
                <ChevronRight className="h-8 w-8" />
              </button>
              <span className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/60 text-xs">
                {lightboxIndex + 1} / {photoUrls.length}
              </span>
            </>
          )}
          <img
            src={photoUrls[lightboxIndex]}
            alt="encomenda"
            className="max-w-[92vw] max-h-[92vh] object-contain rounded-xl shadow-2xl"
            onClick={e => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
}
